import React, { useState, useRef } from 'react';
import { Camera, Loader2, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


/* ───────── Image Compression Utility ───────── */
function compressImage(file, maxWidth = 800, quality = 0.65) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;
        if (w > maxWidth) {
          h = Math.round((h * maxWidth) / w);
          w = maxWidth;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        let dataUrl = canvas.toDataURL('image/webp', quality);
        let mime = 'image/webp';
        if (dataUrl.length < 100) {
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          mime = 'image/jpeg';
        }
        const b64 = dataUrl.split(',')[1];
        resolve({ base64: b64, mime, sizeKB: Math.round(b64.length * 0.75 / 1024) });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ───────── AI Assessment Badge ───────── */
export function AiBadge({ assessment }) {
  if (!assessment || assessment.condition === 'Unknown') return null;
  const condColors = {
    'New': 'text-green-400 bg-green-400/10 border-green-400/20',
    'Good': 'text-primary bg-primary/10 border-primary/20',
    'Fair': 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    'Thrashed': 'text-red-400 bg-red-400/10 border-red-400/20',
  };
  const color = condColors[assessment.condition] || 'text-muted-foreground bg-white/5 border-white/10';
  const pct = Math.round((assessment.confidence || 0) * 100);

  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3" data-testid="ai-assessment-badge">
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles className="w-3.5 h-3.5 text-secondary" />
        <span className="text-[10px] uppercase tracking-widest text-secondary/70 font-semibold">AI Condition Verify</span>
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${color}`}>{assessment.condition}</span>
        <span className="text-[9px] text-muted-foreground/50">{pct}% confidence</span>
      </div>
      {assessment.summary && <p className="text-[11px] text-muted-foreground leading-relaxed">{assessment.summary}</p>}
      {assessment.details && assessment.details.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {assessment.details.map((d, i) => (
            <li key={i} className="text-[10px] text-muted-foreground/60 flex items-start gap-1">
              <span className="text-primary/40 mt-px">-</span> {d}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ───────── Photo Upload Section ───────── */
export default function PhotoUpload({ boardId, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setResult(null);
    try {
      const compressed = await compressImage(file);
      setPreview(`data:${compressed.mime};base64,${compressed.base64}`);
      setUploading(true);
      const res = await authFetch(`${API}/api/boards/${boardId}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image: compressed.base64, mime_type: compressed.mime }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Upload failed');
        setUploading(false);
        return;
      }
      const data = await res.json();
      setResult(data);
      setUploading(false);
      if (onUploaded) onUploaded();
    } catch {
      setError('Failed to process image');
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="photo-upload-section">
      <div className="flex items-center gap-2">
        <button
          data-testid="photo-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className={`text-[10px] px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${uploading ? 'bg-white/5 text-muted-foreground/50' : 'bg-secondary/10 text-secondary border border-secondary/20 hover:bg-secondary/15'}`}
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
          {uploading ? 'Analyzing...' : 'Upload Photo'}
        </button>
        <span className="text-[9px] text-muted-foreground/40">AI will verify condition</span>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFile}
        data-testid="photo-file-input"
      />
      {error && <p className="text-[10px] text-red-400">{error}</p>}
      {(preview || result) && (
        <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
          {preview && (
            <div className="relative rounded-xl overflow-hidden border border-white/10">
              <img src={preview} alt="Board" className="w-full h-32 object-cover" data-testid="photo-preview" />
              {uploading && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <div className="flex items-center gap-2 text-xs text-white">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>AI is inspecting...</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {result?.ai_assessment && <AiBadge assessment={result.ai_assessment} />}
        </motion.div>
      )}
    </div>
  );
}
